import { type ReactNode } from 'react'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { useLearnerBootstrap } from '@/hooks/useLearners'
import { useGlobalState } from '@/stores/globalState'
import { cn } from '@/lib/utils'
import { AppHeader } from './AppHeader'
import { LeftPanel } from './LeftPanel'
import { MiddlePanel } from './MiddlePanel'
import { RightPanel } from './RightPanel'
import { MobileBottomNav } from './MobileBottomNav'
import { CompanionRoot } from '@/components/companion/CompanionRoot'
import { UploadFlowPage } from '@/modules/upload/UploadFlowPage'
import { ASK_HOPSCOTCH, UPLOAD_V2 } from '@/lib/featureFlags'
import { CreateLearnerModal } from '@/components/domain/CreateLearnerModal'
import { DevTools } from '@/components/dev/DevTools'

interface ThreePanelLayoutProps {
  children?: ReactNode
}

export function ThreePanelLayout({ children }: ThreePanelLayoutProps) {
  const isDesktop = useIsDesktop()
  const addLearnerOpen = useGlobalState((s) => s.addLearnerOpen)
  const setAddLearnerOpen = useGlobalState((s) => s.setAddLearnerOpen)
  const uploadEntry = useGlobalState((s) => s.uploadEntry)

  // Pre-select the last-used learner once the list loads (and prompt for the
  // first learner on a fresh account).
  useLearnerBootstrap()

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <AppHeader />

      <div className="flex min-h-0 flex-1">
        {/* Desktop left panel is rendered inline; mobile renders as drawer overlay */}
        <LeftPanel />

        {/* Middle workspace */}
        <MiddlePanel>{children}</MiddlePanel>

        {/* AI chat: Ask Hopscotch companion (push rail / overlay / sheet by
            breakpoint, suppressed during play + evaluation) behind its flag;
            legacy RightPanel otherwise. */}
        {ASK_HOPSCOTCH ? <CompanionRoot /> : <RightPanel />}
      </div>

      {/* Mobile bottom navigation */}
      <MobileBottomNav />

      {/* Add bottom padding on mobile to account for bottom nav */}
      {!isDesktop && (
        <div className={cn('h-16 shrink-0')} aria-hidden="true" />
      )}

      {/* Add-a-learner modal — hosted here so it's always mounted (the
          learner switcher that opens it lives in the mobile drawer, which
          unmounts when closed). */}
      <CreateLearnerModal
        isOpen={addLearnerOpen}
        onClose={() => setAddLearnerOpen(false)}
      />

      {/* Upload-a-book full-page flow — a focused-task overlay above the app
          chrome, driven by store `uploadEntry` (returns null when closed). */}
      {UPLOAD_V2 && uploadEntry && <UploadFlowPage />}

      {/* Dev tools (development only) */}
      {import.meta.env.DEV && <DevTools />}
    </div>
  )
}
